// src/scheduling/services/use-draft-encounters.ts
// The provider's DRAFT (in-progress) encounters — the source for the Today
// worklist's In-Progress bucket (PROD-1300).
//
//   GET /api/Patients/GetEncounterListByDraftStatus?ProviderId={id} → EncounterModel[]
//   404 → [] (mirror the encounter datasource).
//
// Flutter parity: appointment_list_widget.dart getList() decides the middle
// ("draft"/in-progress) bucket by matching each appointment against this list
// (`encounter.appointment == appointment.id`) — NOT by an encounterId on the
// appointment row (the /api/Appointment list wire does not carry one).
import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import type { EncounterModel } from '@/types/encounter-api';

export function draftEncountersKey(providerId: string | null | undefined) {
  return ['draft-encounters', providerId] as const;
}

export function useDraftEncounters(providerId: string | null | undefined) {
  return useQuery<EncounterModel[]>({
    queryKey: draftEncountersKey(providerId),
    enabled: !!providerId,
    queryFn: async ({ signal }) => {
      if (!providerId) return [];
      try {
        return await get<EncounterModel[]>(
          `/api/Patients/GetEncounterListByDraftStatus?ProviderId=${encodeURIComponent(providerId)}`,
          { signal },
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * The set of appointment ids that have a draft (in-progress) encounter — the
 * bucket key the worklist uses. The endpoint already returns only draft-status
 * encounters; we drop soft-deleted ones and rows without an appointment link.
 */
export function draftAppointmentIds(
  encounters: EncounterModel[] | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const e of encounters ?? []) {
    if (e.appointment && !e.isDeleted) ids.add(e.appointment);
  }
  return ids;
}
